import React from "react";
import { PublicLayout } from "@/components/site/PublicLayout";

const SectionHeading = ({ id, children }) => (
  <h2
    id={id}
    className="mt-12 mb-4 font-display text-2xl sm:text-[26px] font-bold tracking-tight text-[color:var(--coh-ink)] scroll-mt-24"
  >
    {children}
  </h2>
);

export default function TermsPage() {
  return (
    <PublicLayout>
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 pb-20">
        <div className="kicker" data-testid="terms-kicker">Terms</div>
        <h1
          data-testid="terms-heading"
          className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[color:var(--coh-ink)]"
        >
          Cohnect &mdash; Terms of Use
        </h1>
        <p className="mt-4 text-sm text-muted-foreground" data-testid="terms-effective-date">
          Effective date: 08/09/2026
        </p>
        <div className="accent-hairline mt-8 max-w-xs" />

        <div className="prose-editorial mt-10 text-[color:var(--coh-ink)]" data-testid="terms-body">
          <p className="lead text-base sm:text-lg leading-relaxed text-muted-foreground">
            <span className="font-semibold text-[color:var(--coh-ink)]">The short version</span>{" "}
            (not a substitute for the full Terms): Cohnect is a free editorial publication about
            human connection. We share ideas, prompts, and message templates &mdash; but every
            decision to contact, meet, or interact with another person is yours alone, and you do
            so at your own risk. Our community is hosted on Discord, an independent platform that
            is not affiliated with us, and people in that community do not speak for Cohnect. The
            Human Count is a fun, self-reported number, not a verified statistic. We provide
            everything as-is, with no warranties and strict limits on our liability.
          </p>

          <SectionHeading id="agreement">1. Agreement to These Terms</SectionHeading>
          <p>
            These Terms of Use (&ldquo;Terms&rdquo;) are a binding agreement between you and Cohnect
            LLC (&ldquo;Cohnect,&rdquo; &ldquo;we,&rdquo;
            &ldquo;us,&rdquo; or &ldquo;our&rdquo;), governing your access to and use of the website
            located at cohnect.com, the weekly issues published there, the Human Count feature, and
            any related content or features we offer (together, the &ldquo;Service&rdquo;). By
            accessing or using the Service, you agree to these Terms and to our Privacy Policy. If
            you do not agree, do not use the Service.
          </p>

          <SectionHeading id="about">2. About Cohnect</SectionHeading>
          <p>
            Cohnect is a free editorial publication. We publish a weekly issue containing
            reflections, reconnection prompts, sample message templates, and small weekly
            challenges. Cohnect does not offer user accounts, paid subscriptions, matchmaking,
            dating services, or any service that introduces you to specific people.
          </p>

          <SectionHeading id="eligibility">3. Eligibility</SectionHeading>
          <p>
            You must be at least 13 years old to use the Service. Participation in the community
            described in Section 6 is intended for individuals 18 years of age or older, and is
            additionally subject to the age requirements and terms of the platform that hosts it.
          </p>

          <SectionHeading id="editorial">4. Editorial Content Only &mdash; Not Advice</SectionHeading>
          <p>
            The Service and all of its content are provided for general informational, editorial,
            and entertainment purposes only. Nothing in the Service constitutes, or is a substitute
            for, medical, mental-health, psychological, therapeutic, counseling, legal, financial,
            or other professional advice, diagnosis, or treatment. No physician-patient,
            therapist-client, or other professional relationship is created by your use of the
            Service. Reading Cohnect, using its prompts, or participating in its community is not
            therapy and is not a mental-health service. If you are struggling, or if you have
            concerns about your health or safety, consult a qualified professional. Never disregard
            professional advice because of something you read in Cohnect.
          </p>

          <SectionHeading id="assumption-of-risk">
            5. Your Interactions With Other People &mdash; Assumption of Risk and Release
          </SectionHeading>
          <p>
            Cohnect&rsquo;s content encourages readers to reach out to people in their own lives
            and to engage with others in the world. Every decision about whether, when, how, and
            with whom to communicate, reconnect, meet, or interact is yours alone.
          </p>
          <p>You acknowledge and agree that:</p>
          <p>
            (a) Cohnect does not select, suggest, introduce, match, screen, vet, supervise, verify
            the identity of, or conduct background checks on any person, including any person you
            contact using our prompts or templates and any person participating in the community
            described in Section 6;
          </p>
          <p>
            (b) Cohnect has no knowledge of your relationships, history, or circumstances, and our
            prompts and templates are generic editorial suggestions, not guidance about any
            specific person or situation;
          </p>
          <p>
            (c) you are solely responsible for the content of any message you send, for choosing
            its recipient, and for the consequences of sending it, including compliance with all
            applicable laws (such as laws regarding harassment, stalking, and unwanted
            communications) and any restraining, protective, or no-contact orders that apply to
            you;
          </p>
          <p>
            (d) interacting with others &mdash; online or offline &mdash; carries inherent risks,
            including the risk of unwelcome, upsetting, offensive, dishonest, or harmful conduct by
            other people, and you assume all such risks in full; and
          </p>
          <p>
            (e) you will use reasonable judgment and caution in all interactions, particularly
            before sharing personal information with, or meeting in person, anyone you do not know
            well.
          </p>
          <p className="legal-caps">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, YOU RELEASE AND FOREVER DISCHARGE COHNECT AND
            THE OTHER RELEASED PARTIES (DEFINED IN SECTION 13) FROM ANY AND ALL CLAIMS, DEMANDS,
            DAMAGES, LOSSES, AND LIABILITIES OF EVERY KIND, KNOWN OR UNKNOWN, SUSPECTED OR
            UNSUSPECTED, ARISING OUT OF OR IN ANY WAY CONNECTED WITH YOUR COMMUNICATIONS,
            INTERACTIONS, RELATIONSHIPS, MEETINGS, OR DISPUTES WITH ANY OTHER PERSON OR ENTITY,
            WHETHER ONLINE OR OFFLINE, AND WHETHER OR NOT PROMPTED, INSPIRED, OR FACILITATED BY THE
            SERVICE, ITS CONTENT, ITS PROMPTS OR TEMPLATES, OR THE COMMUNITY.
          </p>
          <p>
            If you are a California resident, you expressly waive California Civil Code &sect;
            1542, which provides that a general release does not extend to claims that the creditor
            or releasing party does not know or suspect to exist at the time of executing the
            release. Residents of other jurisdictions similarly waive any comparable statute or
            doctrine to the fullest extent permitted by law.
          </p>

          <SectionHeading id="community">
            6. The Community on Discord &mdash; An Independent Third-Party Platform
          </SectionHeading>
          <p>
            We maintain an invite link to a community space hosted on Discord (the
            &ldquo;Community&rdquo;). You acknowledge and agree that:
          </p>
          <p>
            (a) Discord is an independent third-party platform. Discord Inc. is not a partner,
            sponsor, affiliate, agent, or endorser of Cohnect, and Cohnect is not affiliated with,
            endorsed by, or connected to Discord Inc. in any way. &ldquo;Discord&rdquo; and related
            marks are trademarks of Discord Inc., used here solely to identify the platform. We
            simply use Discord as a venue to host the Community, and we may move or discontinue the
            Community at any time;
          </p>
          <p>
            (b) your use of Discord is governed by Discord&rsquo;s own Terms of Service, Community
            Guidelines, and Privacy Policy, which are agreements between you and Discord Inc., not
            with us. We are not responsible for Discord&rsquo;s availability, security, features,
            data practices, or actions;
          </p>
          <p>
            (c) members of the Community are not part of Cohnect. No member, moderator, or
            participant in the Community is an employee, agent, contractor, or representative of
            Cohnect, and none of them is authorized to speak or act on Cohnect&rsquo;s behalf.
            Their statements, content, and conduct are entirely their own. Presence in the
            Community does not create any affiliation with, or endorsement by, Cohnect;
          </p>
          <p>
            (d) we do not and cannot screen Community members, and we have no obligation to
            monitor, moderate, or review the Community or any content posted in it, although we
            reserve the right (but not the duty) to remove content or restrict any person&rsquo;s
            participation at our sole discretion, with or without notice or reason;
          </p>
          <p>
            (e) your participation in the Community is voluntary and entirely at your own risk, and
            Section 5 applies in full to everything that occurs in or arises from the Community;
            and
          </p>
          <p>
            (f) we are not responsible for any content posted in the Community by any person, or
            for any loss, harm, or dispute arising out of the Community.
          </p>

          <SectionHeading id="human-count">7. The Human Count</SectionHeading>
          <p>
            The &ldquo;Human Count&rdquo; is a running number displayed on the Service that
            increases when people indicate &mdash; on the website or through the Community &mdash;
            that they reached out to someone. You acknowledge that the Human Count is
            self-reported, unverified, and approximate; it is provided for motivational and
            entertainment purposes only; it is not a statistic, measurement, or representation of
            fact; and we may adjust, correct, cap, or reset it at any time. You agree not to rely
            on the Human Count for any purpose and not to manipulate or artificially inflate it.
          </p>

          <SectionHeading id="prompts">8. Prompts and Message Templates</SectionHeading>
          <p>
            We grant you a limited, personal, non-exclusive, non-transferable license to use,
            adapt, and send our prompts and message templates in your own personal communications.
            You are solely responsible for every message you send, as described in Section 5.
            Templates are starting points; whether and how you use them is your choice, and we make
            no promise about how any recipient will respond.
          </p>

          <SectionHeading id="third-party">9. Third-Party Links</SectionHeading>
          <p>
            The Service may occasionally link to other websites (for example, to our Discord
            community). We do not control, endorse, or assume responsibility for any third-party
            website, content, product, or service. Accessing third-party content is at your own
            risk and subject to that party&rsquo;s terms and policies.
          </p>

          <SectionHeading id="ip">10. Intellectual Property</SectionHeading>
          <p>
            The Service and its content &mdash; including text, issues, prompts, templates,
            graphics, logos, and the Cohnect name and brand &mdash; are owned by us or our
            licensors and are protected by copyright, trademark, and other laws. Except for the
            license in Section 8, you may access the Service for personal, non-commercial use only.
            You may share links to published issues. You may not republish, reproduce, distribute,
            sell, scrape, or create derivative works from the Service or its content without our
            prior written permission. If you believe content on the Service infringes your
            copyright, contact us at{" "}
            <a href="mailto:info@cohnect.net" className="text-[color:var(--coh-blue)] hover:underline">info@cohnect.net</a>.
          </p>

          <SectionHeading id="acceptable">11. Acceptable Use</SectionHeading>
          <p>
            You agree not to: (a) use the Service for any unlawful purpose or in violation of these
            Terms; (b) manipulate, spam, or artificially inflate the Human Count, including through
            automation; (c) scrape, harvest, or collect data from the Service by automated means;
            (d) interfere with or disrupt the Service, its servers, or its security features;
            (e) impersonate Cohnect or claim any affiliation with, or authority to represent,
            Cohnect; or (f) use the Service&rsquo;s prompts, templates, or Community to harass,
            threaten, deceive, or harm any person.
          </p>

          <SectionHeading id="warranties">12. Disclaimer of Warranties</SectionHeading>
          <p className="legal-caps">
            THE SERVICE AND ALL CONTENT ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
            AVAILABLE,&rdquo; WITHOUT WARRANTIES OF ANY KIND, EXPRESS, IMPLIED, OR STATUTORY,
            INCLUDING WITHOUT LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, AVAILABILITY, OR UNINTERRUPTED OR
            ERROR-FREE OPERATION. WE DO NOT WARRANT THAT THE SERVICE, ITS CONTENT, OR THE COMMUNITY
            WILL MEET YOUR EXPECTATIONS OR PRODUCE ANY PARTICULAR OUTCOME, INCLUDING ANY
            IMPROVEMENT IN YOUR RELATIONSHIPS OR WELL-BEING. SOME JURISDICTIONS DO NOT ALLOW THE
            EXCLUSION OF CERTAIN WARRANTIES, SO SOME OF THE ABOVE EXCLUSIONS MAY NOT APPLY TO YOU.
          </p>

          <SectionHeading id="liability">13. Limitation of Liability</SectionHeading>
          <p className="legal-caps">
            FOR PURPOSES OF THESE TERMS, THE &ldquo;RELEASED PARTIES&rdquo; MEANS COHNECT, ITS
            OPERATOR, AND THEIR RESPECTIVE OWNERS, OFFICERS, EMPLOYEES, CONTRACTORS, VOLUNTEERS,
            LICENSORS, AND SUCCESSORS. TO THE MAXIMUM EXTENT PERMITTED BY LAW: (A) IN NO EVENT WILL
            THE RELEASED PARTIES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
            EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF DATA, GOODWILL, OPPORTUNITY, OR
            EMOTIONAL DISTRESS, ARISING OUT OF OR RELATING TO THE SERVICE, ITS CONTENT, THE HUMAN
            COUNT, THE COMMUNITY, OR YOUR INTERACTIONS WITH ANY PERSON, EVEN IF ADVISED OF THE
            POSSIBILITY OF SUCH DAMAGES; AND (B) THE RELEASED PARTIES&rsquo; TOTAL AGGREGATE
            LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE SERVICE OR THESE TERMS WILL
            NOT EXCEED TEN U.S. DOLLARS (US $10.00), REFLECTING THAT THE SERVICE IS PROVIDED FREE
            OF CHARGE. SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS OF LIABILITY, SO SOME OF
            THE ABOVE MAY NOT APPLY TO YOU; IN THAT CASE, LIABILITY IS LIMITED TO THE SMALLEST
            AMOUNT PERMITTED BY LAW.
          </p>

          <SectionHeading id="indemnification">14. Indemnification</SectionHeading>
          <p>
            You agree to defend, indemnify, and hold harmless the Released Parties from and against
            any claims, liabilities, damages, losses, and expenses (including reasonable
            attorneys&rsquo; fees) arising out of or related to: (a) your use of the Service or the
            Community; (b) your violation of these Terms or of any law; (c) any message you send or
            content you post; or (d) your interactions or disputes with any other person.
          </p>

          <SectionHeading id="changes-termination">15. Changes; Termination</SectionHeading>
          <p>
            We may modify, suspend, or discontinue the Service, the Community, or any feature
            (including the Human Count) at any time, without notice or liability. We may update
            these Terms from time to time by posting a revised version with a new effective date;
            your continued use of the Service after changes are posted constitutes acceptance. We
            may restrict or terminate your access to the Service or the Community at any time, for
            any reason or no reason, without notice.
          </p>

          <SectionHeading id="law">16. Governing Law; Disputes</SectionHeading>
          <p>
            These Terms are governed by the laws of the State of Florida, without regard to
            conflict-of-laws principles. Before filing any claim, you agree to first contact us at{" "}
            <a href="mailto:info@cohnect.net" className="text-[color:var(--coh-blue)] hover:underline">info@cohnect.net</a>{" "}
            and attempt in good faith to resolve the dispute informally for at least thirty (30)
            days. Any dispute that cannot be resolved informally shall be brought exclusively in
            the state or federal courts located in Florida, and you consent to personal
            jurisdiction and venue there, except that either party may bring an individual claim in
            small-claims court. To the fullest extent permitted by law, you and Cohnect each waive
            any right to a jury trial and any right to participate in a class action, class
            arbitration, or other representative proceeding, and any claim must be brought within
            one (1) year after it arises or be permanently barred.
          </p>

          <SectionHeading id="general">17. General</SectionHeading>
          <p>
            If any provision of these Terms is held unenforceable, it will be modified to the
            minimum extent necessary and the remainder will remain in full effect. These Terms and
            the Privacy Policy are the entire agreement between you and us regarding the Service.
            Our failure to enforce any provision is not a waiver. You may not assign these Terms;
            we may assign them freely. Sections that by their nature should survive termination
            (including Sections 5, 6, 7, and 10&ndash;17) survive.
          </p>

          <SectionHeading id="contact">18. Contact</SectionHeading>
          <p>
            Cohnect LLC. For questions about these Terms, support, or complaints, email{" "}
            <a href="mailto:info@cohnect.net" className="text-[color:var(--coh-blue)] hover:underline">info@cohnect.net</a>,
            call{" "}
            <a href="tel:+19416466999" className="text-[color:var(--coh-blue)] hover:underline">+1 941-646-6999</a>, or
            write to us at:
          </p>
          <p>
            Cohnect LLC<br />
            7901 4th St N #13824<br />
            St Petersburg, FL 33702<br />
            United States (Pinellas County)
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
